import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { queryManager } from 'ember-apollo-client';
import deletePurchaseMutation from 'my-phorest/gql/mutations/delete-purchase.graphql';
import voidPurchaseMutation from 'my-phorest/gql/mutations/void-purchase.graphql';
import refundPurchaseMutation from 'my-phorest/gql/mutations/refund-purchase.graphql';
import editPurchaseMutation from 'my-phorest/gql/mutations/edit-purchase.graphql';
import supervisorDeletePurchaseMutation from 'my-phorest/gql/mutations/supervisor-delete-purchase.graphql';
import makeComplimentaryPurchaseMutation from 'my-phorest/gql/mutations/make-complimentary-purchase.graphql';
import updatePurchaseMutation from 'my-phorest/gql/mutations/update-purchase.graphql';
import checkSupervisorDeleteAllowedQuery from 'my-phorest/gql/queries/check-supervisor-delete-allowed.graphql';
import checkMakeComplimentaryAllowedQuery from 'my-phorest/gql/queries/check-make-complimentary-allowed.graphql';
import { dropTask, task } from 'ember-concurrency';
import { service } from '@ember/service';
import { permission } from 'my-phorest/decorators/permission';
import { htmlSafe } from '@ember/template';

const INITIAL_SCREEN = 'transaction-details';
const ERROR_VALIDATION_CODE_AFTER_CASH_UP_SALE = 'AFTER_CASH_UP_SALE';

export default class TransactionSlideOverService extends Service {
  @queryManager apollo;

  @service confirmDialog;
  @service dataFetchManager;
  @service intl;
  @service paymentSlideOver;
  @service router;

  @tracked purchase;

  @tracked isOpen = false;
  @tracked screen = INITIAL_SCREEN;

  @tracked isLoadingReceiptData = false;
  @tracked voidPurchaseModalOpen = false;
  @tracked supervisorDeleteCheck = null;
  @tracked supervisorDeleteModalOpen = false;
  @tracked showReceiptOptionsModal = false;

  @tracked makeComplimentaryCheck = null;
  @tracked makeComplimentaryModalOpen = false;
  @tracked complimentarySaleType = null;

  async openWithPurchase(purchase) {
    this.purchase = purchase;
    this.screen = INITIAL_SCREEN;
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
  }

  get isVoid() {
    return this.purchase.voidingPurchase || this.purchase.voidedPurchase;
  }

  get purchaseHasInteracPayment() {
    return this.purchase.payments.some((payment) =>
      payment.cardTransactions?.some(
        (card) => card.paymentsPaymentMethodType === 'INTERAC_PRESENT'
      )
    );
  }

  @dropTask
  *genericVoidPurchaseTask(voidReason, options = { forceDeleteCashUp: false }) {
    yield this.apollo.mutate(
      {
        mutation: voidPurchaseMutation,
        variables: {
          input: {
            purchaseId: this.purchase.id,
            voidReason,
            ...options,
          },
        },
      },
      'voidPurchase'
    );
  }

  @dropTask
  *voidPurchaseTask(voidReason) {
    try {
      yield this.genericVoidPurchaseTask.perform(voidReason);
    } catch (e) {
      if (
        e.errors?.[0]?.extensions?.validationCode ===
        ERROR_VALIDATION_CODE_AFTER_CASH_UP_SALE
      ) {
        const confirmed = yield this.confirmDialog.ask({
          title: this.intl.t(
            'sales.till-transactions.transaction-details.void-reopen-till-dialog.title'
          ),
          message: htmlSafe(
            this.intl.t(
              'sales.till-transactions.transaction-details.void-reopen-till-dialog.message'
            )
          ),
          continue: this.intl.t('global.continue'),
          cancel: this.intl.t('global.cancel'),
          leadingIcon: 'question-mark-circle',
        });

        if (confirmed) {
          return yield this.genericVoidPurchaseTask.perform(voidReason, {
            forceDeleteCashUp: true,
          });
        } else {
          return false;
        }
      }
    }
  }

  @dropTask
  @permission('purchase.create-refund', {
    guardOnlyOnVariation: 'release-create-purchase-refund',
  })
  *refundPurchaseTask() {
    if (this.purchaseHasInteracPayment) {
      yield this.confirmDialog.ask({
        title: this.intl.t(
          'sales.till-transactions.transaction-details.refund-card-present-dialog.title'
        ),
        message: htmlSafe(
          this.intl.t(
            'sales.till-transactions.transaction-details.refund-card-present-dialog.message'
          )
        ),
        continue: this.intl.t('global.ok'),
        hideCancelButton: true,
        leadingIcon: 'information-circle',
      });
    }

    const terminalId = yield this.paymentSlideOver.getTerminalId.perform();
    const { basket } = yield this.apollo.mutate(
      {
        mutation: refundPurchaseMutation,
        variables: {
          purchaseId: this.purchase.id,
          terminalId,
        },
      },
      'refundPurchase'
    );

    this.paymentSlideOver.returnToSalesEnabled = true;
    this.router.transitionTo('accounts.account.purchase.basket', basket.id);
    this.close();
  }

  @dropTask
  @permission('sales.till-transactions.edit')
  *editPurchaseTask() {
    const terminalId = yield this.paymentSlideOver.getTerminalId.perform();
    const basket = yield this.editPurchaseRequestTask.perform(
      this.purchase.id,
      terminalId
    );

    this.paymentSlideOver.returnToSalesEnabled = true;
    this.router.transitionTo('accounts.account.purchase.basket', basket.id);
    this.close();
  }

  @task
  *editPurchaseRequestTask(purchaseId, terminalId) {
    const { basket } = yield this.apollo.mutate(
      {
        mutation: editPurchaseMutation,
        variables: {
          purchaseId,
          terminalId,
        },
      },
      'editPurchase'
    );
    return basket;
  }

  @dropTask
  *checkSupervisorDeleteAllowed() {
    this.supervisorDeleteCheck = yield this.apollo.query(
      {
        query: checkSupervisorDeleteAllowedQuery,
        variables: {
          purchaseId: this.purchase.id,
        },
      },
      'checkSupervisorDeleteAllowed'
    );
    this.supervisorDeleteModalOpen = true;
  }

  @dropTask
  *supervisorDelete() {
    yield this.apollo.mutate(
      {
        mutation: supervisorDeletePurchaseMutation,
        variables: {
          input: {
            purchaseId: this.purchase.id,
          },
        },
      },
      'supervisorDelete'
    );
  }

  @dropTask
  *undoPurchaseTask() {
    yield this.apollo.mutate(
      {
        mutation: deletePurchaseMutation,
        variables: {
          purchaseId: this.purchase.id,
        },
        update: (cache) => {
          this.#evictPurchase(cache, this.purchase.id);
          cache.gc();
        },
      },
      'deletePurchase'
    );
    this.dataFetchManager.refetchDataFor('purchases');
  }

  #evictPurchase(cache, id) {
    const normalizedId = cache.identify({
      id,
      __typename: 'Purchase',
    });
    cache.evict({ id: normalizedId });
    cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'purchases',
    });
  }

  @dropTask
  *checkMakeComplimentaryAllowed() {
    this.makeComplimentaryCheck = yield this.apollo.query(
      {
        query: checkMakeComplimentaryAllowedQuery,
        variables: {
          purchaseId: this.purchase.id,
        },
      },
      'checkMakeComplimentaryAllowed'
    );
    this.makeComplimentaryModalOpen = true;
  }

  @dropTask
  *makeComplimentaryPurchase() {
    yield this.apollo.mutate(
      {
        mutation: makeComplimentaryPurchaseMutation,
        variables: {
          input: {
            purchaseId: this.purchase.id,
            complimentarySaleType: this.complimentarySaleType,
          },
        },
      },
      'makeComplimentaryPurchase'
    );
  }

  @dropTask
  *saveWithNewDateTime(dateTime) {
    const result = yield this.apollo.mutate(
      {
        mutation: updatePurchaseMutation,
        variables: {
          purchaseId: this.purchase.id,
          input: {
            purchasedAtDateTime: dateTime,
          },
        },
      },
      'updatePurchase'
    );
    this.purchase = result.purchase;
  }
}
